import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const CSRF_ENDPOINT = '/api/auth/csrf';
export const CSRF_HEADER_NAME = 'X-CSRF-TOKEN';
const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

let csrfGeneration = 0;
let csrfToken;
let csrfRequest;

const requestMethod = (args) => {
  if (typeof args === 'string') {
    return 'GET';
  }

  return (args.method || 'GET').toUpperCase();
};

const requestUrl = (args) => typeof args === 'string' ? args : args.url;

const asRequestArgs = (args) => typeof args === 'string' ? { url: args } : args;

const urlOrigin = (url, fallbackUrl) => {
  try {
    return new URL(url, fallbackUrl).origin;
  } catch {
    return undefined;
  }
};

export const isUnsafeMethod = (method = 'GET') => UNSAFE_METHODS.has(method.toUpperCase());

export const isSameBackendRequest = (url, baseUrl, backendBaseUrl) => {
  const backendOrigin = urlOrigin(backendBaseUrl);
  const baseOrigin = urlOrigin(baseUrl, backendBaseUrl);
  const targetOrigin = urlOrigin(url, baseUrl || backendBaseUrl);

  return Boolean(backendOrigin && baseOrigin && targetOrigin
    && baseOrigin === backendOrigin
    && targetOrigin === backendOrigin);
};

export const invalidateCsrfToken = () => {
  csrfGeneration += 1;
  csrfToken = undefined;
};

export const getCachedCsrfToken = () => csrfToken?.token;

const invalidCsrfResponse = () => ({
  error: {
    status: 'PARSING_ERROR',
    originalStatus: 200,
    data: undefined,
    error: 'The CSRF endpoint returned an invalid response.'
  }
});

const loadCsrfToken = async (csrfBaseQuery, api, extraOptions) => {
  if (csrfToken) {
    return { data: csrfToken };
  }

  if (csrfRequest?.generation === csrfGeneration) {
    return csrfRequest.promise;
  }

  const generation = csrfGeneration;
  const promise = csrfBaseQuery({
    url: CSRF_ENDPOINT,
    method: 'GET',
    credentials: 'include'
  }, api, extraOptions).then((result) => {
    if (result.error) {
      return result;
    }

    if (!result.data?.token || result.data.headerName !== CSRF_HEADER_NAME) {
      return invalidCsrfResponse();
    }

    if (generation === csrfGeneration) {
      csrfToken = result.data;
    }

    return result;
  }).finally(() => {
    if (csrfRequest?.promise === promise) {
      csrfRequest = undefined;
    }
  });

  csrfRequest = { generation, promise };
  return promise;
};

export const createBffBaseQuery = ({
  baseUrl,
  backendBaseUrl = process.env.REACT_APP_BACKEND_BASE_URL,
  fetchFn
}) => {
  const queryOptions = { baseUrl, credentials: 'include' };
  const csrfQueryOptions = { baseUrl: backendBaseUrl, credentials: 'include' };

  if (fetchFn) {
    queryOptions.fetchFn = fetchFn;
    csrfQueryOptions.fetchFn = fetchFn;
  }

  const baseQuery = fetchBaseQuery(queryOptions);
  const csrfBaseQuery = fetchBaseQuery(csrfQueryOptions);

  return async (args, api, extraOptions = {}) => {
    const sameBackend = isSameBackendRequest(
      requestUrl(args),
      baseUrl,
      backendBaseUrl
    );
    let preparedArgs = asRequestArgs(args);

    if (!sameBackend) {
      return baseQuery({ ...preparedArgs, credentials: 'omit' }, api, extraOptions);
    }

    preparedArgs = { ...preparedArgs, credentials: 'include' };

    if (isUnsafeMethod(requestMethod(preparedArgs))) {
      const csrfResult = await loadCsrfToken(csrfBaseQuery, api, extraOptions);
      if (csrfResult.error) {
        return csrfResult;
      }

      const headers = new Headers(preparedArgs.headers);
      headers.set(CSRF_HEADER_NAME, csrfResult.data.token);
      preparedArgs = { ...preparedArgs, headers };
    }

    const result = await baseQuery(preparedArgs, api, extraOptions);

    if (!result.error && extraOptions.refreshCsrfAfterResponse) {
      invalidateCsrfToken();
      await loadCsrfToken(csrfBaseQuery, api, extraOptions);
    }

    return result;
  };
};
